import { searchClient } from '@algolia/client-search';
import invariant from 'invariant';
import queryString from 'query-string';

import { RichError } from '#/utils/RichError';
import { getJson } from '#/utils/http/getJson';
import { trackError } from '#/utils/trackError';

import {
  AlgoliaValidUntilError,
  isValidUntilError,
} from './AlgoliaValidUntilError';

type Args = {
  config: Readonly<{ appId: string; apiKey: string }>;
  index: string;
  ids: string[];
  attributes: string[];
  isUsingAlgoliaMock: boolean;
};

export async function algoliaGetObjects<T>({
  config: { appId, apiKey },
  index,
  ids,
  attributes,
  isUsingAlgoliaMock,
}: Args): Promise<(T | null)[]> {
  if (isUsingAlgoliaMock) {
    const idsParams = ids.map((id) => `object_id=${id}`).join('&');
    const url = `http://algoliamock:6577/${index}/objects?${idsParams}`;
    return getJson<{ results: (T | null)[] }>(url).then(
      (response) => response.results,
    );
  }
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');
  const client = searchClient(appId, apiKey);

  return client
    .getObjects<T>({
      requests: ids.map((id) => ({
        indexName: index,
        objectID: id,
        attributesToRetrieve: attributes,
      })),
    })
    .then(({ results }) => results)
    .catch((e) => {
      if (isValidUntilError(e)) {
        throw new AlgoliaValidUntilError();
      }

      trackError(
        new RichError('Algolia POST request error.', {
          errorMessage: e.message,
          requestParams: queryString.stringify({
            ids,
            attributes,
          }),
          statusCode: e.statusCode,
          response: e.response,
        }),
      );

      return [];
    });
}
